import { uiText } from '@/i18n/interface';

import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";

interface AdminCodeValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

interface SecurityAttempt {
  type: 'admin_code' | 'login' | 'password_reset';
  success: boolean;
  email?: string;
  metadata?: Record<string, any>;
}

export const useSecureAuth = () => {
  const validateAdminCodeSecure = async (
    adminCode: string,
    email: string
  ): Promise<AdminCodeValidationResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('secure-admin-validator', {
        body: {
          adminCode,
          email,
          userAgent: navigator.userAgent
        }
      });

      if (error) {
        console.error('Admin code validation error:', error);
        return {
          success: false,
          error: 'Failed to validate admin code. Please try again.'
        };
      }

      return data;
    } catch (error) {
      console.error('Unexpected validation error:', error);
      return {
        success: false,
        error: 'An unexpected error occurred during validation.'
      };
    }
  };

  const logSecurityAttempt = async (attempt: SecurityAttempt) => {
    try {
      // Log security attempts through a secure function call
      await supabase.rpc('log_security_attempt', {
        user_ip: '0.0.0.0', // Will be filled by the database function
        attempt_type: attempt.type,
        success_status: attempt.success,
        user_email: attempt.email,
        user_agent_string: navigator.userAgent,
        attempt_metadata: attempt.metadata || {}
      });
    } catch (error) {
      console.error('Failed to log security attempt:', error);
    }
  };

  const generateSecureAdminCode = async (
    description: string = 'Generated admin code',
    maxUses: number = 5,
    validDays: number = 30
  ): Promise<string | null> => {
    try {
      const { data, error } = await supabase.rpc('generate_secure_admin_code', {
        description_text: description,
        max_usage: maxUses,
        valid_days: validDays
      });

      if (error) {
        console.error('Error generating admin code:', error);
        toast({
          title: uiText("Generation Failed"),
          description: uiText("Failed to generate admin code. You may not have permission."),
          variant: "destructive"
        });
        return null;
      }

      return data;
    } catch (error) {
      console.error('Unexpected error generating admin code:', error);
      return null;
    }
  };

  const validatePasswordStrength = async (password: string) => {
    try {
      const { data, error } = await supabase.rpc('validate_password_strength', {
        password
      });

      if (error) {
        console.error('Password validation error:', error);
        return {
          valid: false,
          errors: ['Failed to validate password strength']
        };
      }

      return data;
    } catch (error) {
      console.error('Unexpected password validation error:', error);
      return {
        valid: false,
        errors: ['An unexpected error occurred during password validation']
      };
    }
  };

  return {
    validateAdminCodeSecure,
    logSecurityAttempt,
    generateSecureAdminCode,
    validatePasswordStrength
  };
};
